import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useModulesSummary, useTodayCards } from '@/api/dashboard';
import {
  FixedNav,
  Caption,
  Card,
  Divider,
  StatStrip,
  AnimatedNumber,
  PulseDot,
  OnboardingModal,
  MODULE_SLIDES,
} from '@/ui';
import { openingLabApi } from './api';
import type { Color, Rarity, Repertoire } from './types';

/**
 * Opening Lab — liste des répertoires (port V2).
 *
 * Source proto : `docs/prototypes/ui-kit-v2/.../OpeningLab.jsx` §OpeningLabList.
 * Pattern visuel commun : PulseDot violet + display-2 + StatStrip + filter tabs
 * + grid de tiles. Filter par couleur (Blancs/Noirs), rareté (rares), cartes
 * dues — la donnée mastery par rep et l'autopsy chip "12 parties · 3 blunder"
 * nécessitent enrichissement backend (T2 KPI nocturnes) donc omis en V2.
 *
 * Wiring : `useQuery(['opening-list'])` (existant MVP1.0) + `useModulesSummary
 * (30).modules.opening.mastery_pct` pour la maîtrise moyenne + `useTodayCards()
 * .opening_due_count` pour les cartes dues.
 */

const RARITY_META: Record<Rarity, { label: string; color: string; tone: 'violet' | 'volt' | 'amber' }> = {
  classic: { label: 'classique', color: 'var(--violet-500)', tone: 'violet' },
  classic_revisited: { label: 'classique revisité', color: 'var(--violet-500)', tone: 'violet' },
  rare_sound: { label: 'rare sain', color: 'var(--volt-500)', tone: 'volt' },
  rare_risky: { label: 'rare risqué', color: 'var(--amber-500)', tone: 'amber' },
};

type Filter = 'all' | Color | 'rare';

export function RepertoireListPage(): JSX.Element {
  const navigate = useNavigate();
  const listQ = useQuery({
    queryKey: ['opening-list'],
    queryFn: () => openingLabApi.list(),
  });
  const modulesQ = useModulesSummary(30);
  const todayQ = useTodayCards();

  const reps = useMemo(() => listQ.data?.repertoires ?? [], [listQ.data]);
  const total = reps.length;
  const counts = useMemo(
    () => ({
      white: reps.filter((r) => r.color === 'white').length,
      black: reps.filter((r) => r.color === 'black').length,
      rare: reps.filter((r) => r.rarity.startsWith('rare')).length,
    }),
    [reps],
  );

  const avgMastery = Math.round(modulesQ.data?.modules.opening.mastery_pct ?? 0);
  const cardsDue = todayQ.data?.opening_due_count ?? 0;

  const [filter, setFilter] = useState<Filter>('all');
  const visible = useMemo(() => {
    if (filter === 'white') return reps.filter((r) => r.color === 'white');
    if (filter === 'black') return reps.filter((r) => r.color === 'black');
    if (filter === 'rare') return reps.filter((r) => r.rarity.startsWith('rare'));
    return reps;
  }, [reps, filter]);

  const filters: Array<{ id: Filter; label: string; count: number }> = [
    { id: 'all', label: 'Tous', count: total },
    { id: 'white', label: 'Blancs', count: counts.white },
    { id: 'black', label: 'Noirs', count: counts.black },
    { id: 'rare', label: 'Rares', count: counts.rare },
  ];

  const chips = [
    { label: 'Répertoires', value: String(total), color: 'var(--paper-50)' },
    { label: 'Cartes dues', value: String(cardsDue), color: 'var(--acid-500)' },
  ];

  return (
    <>
      {/* T7 MVP2.5 — onboarding 1ère visite Opening Lab (bug A8). */}
      <OnboardingModal
        moduleSlug="opening-lab"
        moduleLabel="Opening Lab"
        tone="violet"
        slides={MODULE_SLIDES['opening-lab'] ?? []}
      />
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--violet-500)" size={8} />
            <Caption color="violet">Opening Lab</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">
            Répertoire d'ouvertures
          </h1>
          <p className="text-body-sm text-ink-300">
            {total} répertoires actifs · les rares sont stress-testées par Stockfish 16 NNUE +
            base Masters (≥ 50 parties à ≥ 2200).
          </p>
        </header>

        <StatStrip className="flex-wrap items-stretch gap-x-8">
          <StatTile label="Répertoires" value={total} color="var(--paper-50)" />
          <Divider vertical />
          <StatTile
            label="Maîtrise · 30j"
            value={avgMastery}
            color="var(--violet-500)"
            format={(v) => `${Math.round(v)}%`}
          />
          <Divider vertical />
          <StatTile label="Cartes dues" value={cardsDue} color="var(--acid-500)" />
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[200px]">
            <Caption>Source officielle</Caption>
            <span className="font-mono text-[11px] leading-snug text-ink-300">
              <span className="text-paper-50">atlas_ouvertures_letales.pdf</span>
              <br />
              <span className="text-paper-50">60</span> lignes notées /10
            </span>
          </div>
        </StatStrip>

        {/* Filter tabs */}
        <nav className="flex gap-2 flex-wrap">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg border font-display text-sm transition-all duration-150 ${
                  active
                    ? 'border-violet-500 bg-violet-500/10 text-paper-50'
                    : 'border-ink-700 bg-ink-800 text-ink-300 hover:border-ink-600 hover:text-paper-50'
                }`}
              >
                {f.label}
                <span className="ml-1.5 font-mono text-xs opacity-70">{f.count}</span>
              </button>
            );
          })}
        </nav>

        {listQ.isPending && (
          <Card>
            <p className="text-ink-300 italic">Chargement des répertoires…</p>
          </Card>
        )}
        {listQ.isError && (
          <Card>
            <p className="text-coral-500">Erreur de chargement.</p>
          </Card>
        )}

        {visible.length > 0 && (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0">
            {visible.map((r) => (
              <li key={r.slug}>
                <RepertoireTile
                  rep={r}
                  onClick={() => navigate(`/opening-lab/${r.slug}`)}
                  onAutopsy={() => navigate(`/game-autopsy?opening_slug=${encodeURIComponent(r.slug)}`)}
                />
              </li>
            ))}
          </ul>
        )}

        <footer className="pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Mission Control
          </button>
        </footer>
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  color,
  format,
}: {
  label: string;
  value: number;
  color: string;
  format?: (v: number) => string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-[80px]">
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        {...(format ? { format } : {})}
        className="font-mono"
        style={{ fontSize: 26, color, lineHeight: 1, fontWeight: 500 }}
      />
    </div>
  );
}

function RepertoireTile({
  rep,
  onClick,
  onAutopsy,
}: {
  rep: Repertoire;
  onClick: () => void;
  onAutopsy: () => void;
}): JSX.Element {
  const rarity = RARITY_META[rep.rarity];
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left flex flex-col gap-3 p-5 bg-ink-800 border border-ink-700 rounded-xl transition-all duration-150 hover:border-violet-500/50 hover:shadow-card-hover animate-card-enter w-full"
    >
      <div className="flex justify-between items-center gap-2">
        <Caption color="violet">
          {rep.color === 'white' ? 'Blancs' : 'Noirs'}
          {rep.eco_code ? ` · ${rep.eco_code}` : ''}
        </Caption>
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-mono"
          style={{ color: rarity.color, borderColor: `${rarity.color}66`, backgroundColor: `${rarity.color}10` }}
        >
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full"
            style={{ background: rarity.color }}
          />
          {rarity.label}
        </span>
      </div>
      <h2 className="font-display text-heading-3 text-paper-50 leading-snug">
        {rep.display_name}
      </h2>
      <div className="mt-auto flex flex-col gap-2">
        <div className="flex items-center justify-between font-mono text-[11px] text-ink-300">
          <span>{rep.family}</span>
          <span className="text-violet-500">Exercice →</span>
        </div>
        {/* Bi-directional Autopsy chip — backend opening_slug filter OK (#48) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAutopsy();
          }}
          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-coral-500/20 bg-coral-500/[0.04] hover:border-coral-500/50 transition-all duration-150 font-mono text-[11px]"
        >
          <span aria-hidden="true">🔍</span>
          <span className="text-ink-300">Voir parties</span>
          <span className="ml-auto text-coral-500">Analyser →</span>
        </button>
      </div>
    </button>
  );
}
